import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/lib/constants";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShoppingCart, Menu, X, User, Package, LogOut, MapPin, Search } from "lucide-react";

export function Header() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/products${q.trim() ? `?search=${encodeURIComponent(q.trim())}` : ""}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-cream/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:gap-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="logo-home-link">
          <img src="/nicee-logo.jpg" alt="Nicee" className="h-10 w-10 rounded-xl object-cover sm:h-11 sm:w-11" />
        </Link>

        <form onSubmit={submitSearch} className="relative flex-1 max-w-xl" data-testid="header-search-form">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Search "milk", "atta", "snacks"...'
            data-testid="header-search-input"
            className="h-10 w-full rounded-full border border-border bg-white pl-9 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand"
          />
        </form>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <Link to="/cart" className="relative" data-testid="cart-link" aria-label="Cart">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ShoppingCart className="h-5 w-5 text-ink" />
            </Button>
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-navy px-1 text-xs font-semibold text-white">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="account-menu">
                  <User className="h-5 w-5 text-ink" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-semibold text-ink">{user.name}</p>
                  <p className="text-xs text-ink-muted">{user.mobile || user.email}</p>
                </div>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Package className="mr-2 h-4 w-4" /> Admin Dashboard
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/my-orders")} data-testid="menu-my-orders">
                      <Package className="mr-2 h-4 w-4" /> My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/track")}>
                      <MapPin className="mr-2 h-4 w-4" /> Track Order
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => navigate("/login")} data-testid="header-login-btn" className="hidden rounded-full bg-navy px-5 font-semibold text-white hover:bg-navy-hover sm:inline-flex">
              Login
            </Button>
          )}

          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-border/60 bg-cream"
          >
            <div className="mx-auto flex max-w-7xl flex-col p-4 sm:px-6 lg:px-8">
              {[...NAV_LINKS, { label: "Track My Order", to: "/track" }].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-4 py-3 text-ink-soft hover:bg-cream-alt"
                  end={l.to === "/"}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
              {!user && (
                <div className="mt-2 flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setOpen(false); navigate("/login"); }}>Login</Button>
                  <Button className="flex-1 rounded-full bg-brand font-semibold text-ink" onClick={() => { setOpen(false); navigate("/register"); }} data-testid="header-register-btn">Sign up</Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
